import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import api from '../../lib/request';

const CONNECTIONS_URL = '/api/v1/settings/connections';
const PROVIDERS = { openai: 'OpenAI', deepseek: 'DeepSeek' };
const REQUEST_OPTIONS = { timeout: 20000 };

function readableConnections(value) {
  return value?.schemaVersion === 2 && Number.isSafeInteger(value.revision)
    && value.revision >= 0 && Array.isArray(value.items) ? value : null;
}

function validateForm(form) {
  // Match the backend name boundary, including its rejection of ASCII controls.
  // eslint-disable-next-line no-control-regex
  if (form.mode !== 'replace' && (!form.name.trim() || form.name.length > 80 || /[<>\x00-\x1f\x7f]/.test(form.name))) {
    return 'Enter a name of 1 to 80 characters without markup or control characters.';
  }
  if (form.mode !== 'edit') {
    const key = form.apiKey.trim();
    if (!/^[\x21-\x7e]{20,300}$/.test(key) || (form.provider === 'openai' && !key.startsWith('sk-'))) {
      return `Enter a valid ${PROVIDERS[form.provider]} API key (20–300 characters, no spaces).`;
    }
  }
  return '';
}

export default function PersonalConnections({ initialConnections, initialUnavailable = false }) {
  const [connections, setConnections] = useState(() => readableConnections(initialConnections));
  const [unavailable, setUnavailable] = useState(initialUnavailable);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [success, setSuccess] = useState('');
  const [verified, setVerified] = useState({});
  const [form, setForm] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const mountedRef = useRef(false);
  const connectionsRef = useRef(connections);
  const readSequence = useRef(0);
  const operationSequence = useRef(0);
  const busyRef = useRef(false);
  const feedbackRef = useRef(null);

  const acceptConnections = useCallback((value) => {
    const next = readableConnections(value);
    if (!next) throw new Error('Connection details are unavailable. Refresh and try again.');
    // A focus read started before a mutation must not roll the UI back afterward.
    if (connectionsRef.current && next.revision < connectionsRef.current.revision) return connectionsRef.current;
    if (connectionsRef.current?.revision !== next.revision) setVerified({});
    connectionsRef.current = next;
    setConnections(next);
    setUnavailable(false);
    setRefreshError('');
    return next;
  }, []);

  const refresh = useCallback(async () => {
    const sequence = ++readSequence.current;
    setRefreshing(true);
    try {
      const response = await api.get('/api/v1/settings', REQUEST_OPTIONS);
      if (!mountedRef.current || sequence !== readSequence.current) return null;
      if (response?.settingsUnavailable) throw new Error('Secure connection storage is unavailable. Refresh to try again.');
      return acceptConnections(response?.settings?.connections);
    } catch (err) {
      if (mountedRef.current && sequence === readSequence.current) {
        setUnavailable(true);
        setVerified({});
        setRefreshError(err?.message || 'Unable to refresh connections. Check your connection and try again.');
      }
      return null;
    } finally {
      if (mountedRef.current && sequence === readSequence.current) setRefreshing(false);
    }
  }, [acceptConnections]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const onFocus = () => { refresh(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      readSequence.current += 1;
      operationSequence.current += 1;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  async function perform(action, request, onSuccess, expectedRevision) {
    if (busyRef.current || unavailable || !connectionsRef.current) return;
    busyRef.current = true;
    const sequence = ++operationSequence.current;
    setBusy(action);
    setError('');
    setSuccess('');
    if (action.startsWith('test:')) {
      setVerified((previous) => {
        const next = { ...previous };
        delete next[action.slice(5)];
        return next;
      });
    }
    try {
      const result = await request();
      if (!mountedRef.current || sequence !== operationSequence.current) return;
      if (result?.error) throw result;
      if (action.startsWith('test:')) {
        const test = result?.connectionTest;
        const item = connectionsRef.current.items.find((entry) => entry.id === action.slice(5));
        if (connectionsRef.current.revision !== expectedRevision) {
          const conflict = new Error('Connections changed while the test was running.');
          conflict.errorCode = 'connections_revision_conflict';
          throw conflict; // Never attribute an old result to the current key.
        }
        if (!test?.verified || test.revision !== expectedRevision || test.connectionId !== item?.id || test.provider !== item?.provider) {
          throw new Error('The connection test result is no longer current. Test again.');
        }
        setVerified((previous) => ({ ...previous, [test.connectionId]: test }));
      } else {
        // Invalidate outstanding reads, including errors based on the old state.
        readSequence.current += 1;
        setRefreshing(false);
        acceptConnections(result?.settings?.connections);
      }
      onSuccess(result);
    } catch (err) {
      if (!mountedRef.current || sequence !== operationSequence.current) return;
      if (err?.errorCode === 'connections_revision_conflict' || err?.errorCode === 'connections_migration_required') {
        const next = await refresh();
        if (!mountedRef.current || sequence !== operationSequence.current) return;
        const message = 'Connections changed in another tab. Review the updated list, then try the action again. Your form has been kept.';
        setError(message);
        setForm((previous) => previous ? { ...previous, expectedRevision: next?.revision ?? previous.expectedRevision, error: message } : previous);
        setConfirmation((previous) => previous ? { ...previous, expectedRevision: next?.revision ?? previous.expectedRevision, error: message } : previous);
      } else {
        const message = err?.message || 'The connection action failed. Please try again.';
        setError(message);
        setForm((previous) => previous ? { ...previous, error: message } : previous);
        setConfirmation((previous) => previous ? { ...previous, error: message } : previous);
      }
    } finally {
      if (mountedRef.current && sequence === operationSequence.current) {
        busyRef.current = false;
        setBusy('');
      }
    }
  }

  function openForm(mode, item) {
    setForm({ mode, id: item?.id, name: item?.name || '', provider: item?.provider || 'openai', apiKey: '', expectedRevision: connections.revision, error: '' });
    setError('');
    setSuccess('');
  }

  function saveForm(event) {
    event.preventDefault();
    const validationError = validateForm(form);
    if (validationError) {
      setForm((previous) => ({ ...previous, error: validationError }));
      return;
    }
    const payload = { expectedRevision: form.expectedRevision };
    if (form.mode !== 'replace') payload.name = form.name;
    if (form.mode !== 'edit') payload.apiKey = form.apiKey;
    if (form.mode === 'create') payload.provider = form.provider;
    perform('save', () => form.mode === 'create'
      ? api.post(CONNECTIONS_URL, payload, REQUEST_OPTIONS)
      : api.patch(`${CONNECTIONS_URL}/${form.id}`, payload, REQUEST_OPTIONS), () => {
      setForm(null); // Forget the entered secret on success and on dismiss.
      setSuccess(form.mode === 'replace' ? 'API key replaced. Test this key to verify access.' : 'Connection saved. Saving does not verify provider access.');
    });
  }

  function confirmAction() {
    const { item, mode, expectedRevision } = confirmation;
    if (mode === 'delete') {
      perform('delete', () => api.delete(`${CONNECTIONS_URL}/${item.id}`, { ...REQUEST_OPTIONS, data: { expectedRevision } }), () => {
        setConfirmation(null);
        setSuccess('Connection deleted. No other connection was activated automatically.');
      });
    } else {
      perform(`test:${item.id}`, () => api.post(`${CONNECTIONS_URL}/${item.id}/test`, { expectedRevision }, { timeout: 25000 }), () => {
        setConfirmation(null);
        setSuccess(`Test succeeded for ${item.name}. This does not guarantee future calls, balance or image support.`);
      }, expectedRevision);
    }
  }

  const items = connections?.items || [];
  const disabled = unavailable || !connections || Boolean(busy) || refreshing;
  const editedItem = form?.id ? items.find((item) => item.id === form.id) : null;
  const confirmedItem = confirmation ? items.find((item) => item.id === confirmation.item.id) : null;
  const formTitle = form?.mode === 'create' ? 'Add connection' : form?.mode === 'replace' ? 'Replace API key' : 'Edit connection';

  return (
    <Card className="settings-module-card">
      <Card.Body>
        <div className="settings-module-header">
          <h1>Connections</h1>
          <p>Manage your personal AI accounts. Only the active connection is used for new generations.</p>
        </div>
        <div ref={feedbackRef} tabIndex={-1} aria-live="polite">
          {error && !form && !confirmation ? <Alert variant="danger">{error}</Alert> : null}
          {success ? <Alert variant="success">{success}</Alert> : null}
        </div>
        {unavailable ? <Alert variant="warning">Connection details cannot be confirmed. {refreshError || 'Refresh before making changes.'}</Alert> : null}
        {!unavailable && connections && !connections.activeConnectionId ? <Alert variant="info">No active connection. {items.length ? 'Activate a saved connection to generate with AI.' : 'Add your first connection to enable AI generation.'}</Alert> : null}
        <div className="settings-connections-toolbar">
          <Button onClick={() => openForm('create')} disabled={disabled || items.length >= 10}>Add connection</Button>
          <Button variant="outline-secondary" disabled={Boolean(busy) || refreshing} onClick={() => { setError(''); refresh(); }}>
            {refreshing ? <><Spinner animation="border" size="sm" aria-hidden="true" /> Refreshing…</> : 'Refresh connections'}
          </Button>
        </div>
        <p className="small text-muted mt-3">Keys are encrypted and never shown again. Saving does not verify access or balance. Tests send only synthetic text and may use a small number of tokens.</p>
        <div className="settings-connection-list" aria-busy={refreshing || Boolean(busy)}>
          {items.map((item) => (
            <Card className="settings-provider-card" key={item.id} as="section" aria-label={`${item.name} connection`}>
              <Card.Body>
                <div className="settings-connection-heading">
                  <h2 className="h5">{item.name}</h2>
                  {connections.activeConnectionId === item.id ? <Badge bg="primary">Active</Badge> : null}
                </div>
                <p className="mb-1">{PROVIDERS[item.provider] || item.provider} · Key saved <span className="settings-key-preview">{item.apiKeyPreview}</span></p>
                <p className="small text-muted mb-0">{verified[item.id] ? `Verified in this session with ${verified[item.id].model}. Future calls may still fail.` : 'Not verified in this session.'}</p>
                <div className="settings-actions mt-3">
                  {connections.activeConnectionId !== item.id ? <Button variant="outline-primary" size="sm" disabled={disabled} onClick={() => perform('activate', () => api.put(`${CONNECTIONS_URL}/active`, { connectionId: item.id, expectedRevision: connections.revision }, REQUEST_OPTIONS), () => setSuccess(`${item.name} is now active for new generations.`))}>Activate</Button> : null}
                  <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={() => openForm('edit', item)}>Edit name</Button>
                  <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={() => openForm('replace', item)}>Replace key</Button>
                  <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={() => setConfirmation({ mode: 'test', item, expectedRevision: connections.revision, error: '' })}>Test connection</Button>
                  <Button variant="outline-danger" size="sm" disabled={disabled} onClick={() => setConfirmation({ mode: 'delete', item, expectedRevision: connections.revision, error: '' })}>Delete</Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      </Card.Body>

      <Modal show={Boolean(form)} onHide={() => { if (!busy) setForm(null); }} centered backdrop={busy ? 'static' : true} keyboard={!busy} aria-labelledby="connection-form-title" onExited={() => feedbackRef.current?.focus()}>
        <Modal.Header closeButton={!busy}><Modal.Title id="connection-form-title">{formTitle}</Modal.Title></Modal.Header>
        {form ? <Form onSubmit={saveForm} noValidate>
          <Modal.Body>
            {unavailable ? <Alert variant="warning">{refreshError || 'Connection details cannot be confirmed.'} <Button size="sm" variant="outline-secondary" disabled={Boolean(busy) || refreshing} onClick={refresh}>Retry loading connections</Button></Alert> : null}
            {form.error ? <Alert variant="danger">{form.error}</Alert> : null}
            {form.id && !editedItem ? <Alert variant="warning">This connection was deleted. Close this form and add a new connection.</Alert> : null}
            {form.id && editedItem && form.expectedRevision !== connections?.revision ? <Alert variant="warning">The saved connections changed. Your entries are preserved; submitting will check for a conflict.</Alert> : null}
            {form.id && editedItem ? <p className="small text-muted">Currently saved: {editedItem.name} ({PROVIDERS[editedItem.provider]}). {editedItem.apiKeyPreview}</p> : null}
            {form.mode === 'create' ? <Form.Group className="mb-3" controlId="connection-provider">
              <Form.Label>Provider</Form.Label>
              <Form.Select value={form.provider} disabled={Boolean(busy)} onChange={(event) => setForm({ ...form, provider: event.target.value, apiKey: '', error: '' })}>
                <option value="openai">OpenAI</option><option value="deepseek">DeepSeek</option>
              </Form.Select>
            </Form.Group> : <p>Provider: <strong>{PROVIDERS[form.provider]}</strong>. To use another provider, add a connection.</p>}
            {form.mode !== 'replace' ? <Form.Group className="mb-3" controlId="connection-name">
              <Form.Label>Connection name</Form.Label>
              <Form.Control autoFocus value={form.name} maxLength={80} disabled={Boolean(busy)} onChange={(event) => setForm({ ...form, name: event.target.value, error: '' })} placeholder="For example, Personal account" />
            </Form.Group> : null}
            {form.mode !== 'edit' ? <Form.Group controlId="connection-key">
              <Form.Label>{form.mode === 'replace' ? 'New API key' : 'API key'}</Form.Label>
              <Form.Control autoFocus={form.mode === 'replace'} type="password" value={form.apiKey} maxLength={300} autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} disabled={Boolean(busy)} onChange={(event) => setForm({ ...form, apiKey: event.target.value, error: '' })} aria-describedby="connection-key-help" />
              <Form.Text id="connection-key-help">The key is encrypted at rest. It is cleared from this form when saved or closed. {form.mode === 'replace' ? 'Replacing it requires a new test.' : ''}</Form.Text>
            </Form.Group> : null}
            {form.mode === 'create' ? <p className="small text-muted mt-3 mb-0">{items.length ? 'This connection will be saved inactive. Activate it explicitly when ready.' : 'Your first connection becomes active automatically.'} Saving does not contact the provider.</p> : null}
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" disabled={Boolean(busy)} onClick={() => setForm(null)}>Cancel</Button><Button type="submit" disabled={disabled || Boolean(form.id && !editedItem)}>{busy === 'save' ? 'Saving…' : form.mode === 'replace' ? 'Replace key' : 'Save connection'}</Button></Modal.Footer>
        </Form> : null}
      </Modal>

      <Modal show={Boolean(confirmation)} onHide={() => { if (!busy) setConfirmation(null); }} centered backdrop={busy ? 'static' : true} keyboard={!busy} aria-labelledby="connection-confirm-title" onExited={() => feedbackRef.current?.focus()}>
        <Modal.Header closeButton={!busy}><Modal.Title id="connection-confirm-title">{confirmation?.mode === 'delete' ? 'Delete connection?' : 'Test connection?'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {unavailable ? <Alert variant="warning">{refreshError || 'Connection details cannot be confirmed.'} <Button size="sm" variant="outline-secondary" disabled={Boolean(busy) || refreshing} onClick={refresh}>Retry loading connections</Button></Alert> : null}
          {confirmation?.error ? <Alert variant="danger">{confirmation.error}</Alert> : null}
          {confirmation && !confirmedItem ? <Alert variant="warning">This connection was deleted in another tab.</Alert> : null}
          <p><strong>{confirmedItem?.name || confirmation?.item.name}</strong> · {PROVIDERS[confirmation?.item.provider]}</p>
          {confirmation?.mode === 'delete' ? <><p>The saved API key will be removed.</p>{connections?.activeConnectionId === confirmation?.item.id ? <Alert variant="warning">This is the active connection. AI generation will be unavailable until you explicitly activate another connection. No replacement is selected automatically.</Alert> : null}</> : <p>A small synthetic request will be sent to {PROVIDERS[confirmation?.item.provider]} using this API account. It may consume a small number of tokens. No guide content is sent and the active connection stays unchanged.</p>}
        </Modal.Body>
        <Modal.Footer><Button autoFocus variant="secondary" disabled={Boolean(busy)} onClick={() => setConfirmation(null)}>Cancel</Button><Button variant={confirmation?.mode === 'delete' ? 'danger' : 'primary'} disabled={disabled || !confirmedItem} onClick={confirmAction}>{busy ? 'Working…' : confirmation?.mode === 'delete' ? 'Delete connection' : 'Run test'}</Button></Modal.Footer>
      </Modal>
    </Card>
  );
}
